import { Maximize2, Sparkles, X } from 'lucide-react'
import { type KeyboardEvent } from 'react'

import { useAlerts } from '../api/control'
import { usePortfolioAdvicePresets } from '../api/bots'
import type { Alert } from '../api/types'
import { useAdvisor } from '../lib/advisor'
import { formatDateTime } from '../lib/format'
import { AdvisorConversation } from './AdvisorConversation'
import { parseAdviceContext } from './PortfolioAdvisor'

/** Las lecturas del asesor, más cuál se muestra: la elegida si todavía existe,
 *  si no la más reciente. Compartido por el panel y por la ventana. */
export function useActiveReading() {
  const advisor = useAdvisor()
  const { data, isPending } = useAlerts(false, 'portfolio_advice')
  const readings = data ?? []
  const active = readings.find((alert) => alert.id === advisor.activeAlertId) ?? readings[0]
  return { readings, active, isPending }
}

function readingLabel(alert: Alert): string {
  const label = parseAdviceContext(alert)?.presetLabel ?? alert.title
  const repeats = alert.followup_history.length
  const suffix = repeats ? ` · ${repeats} repregunta${repeats === 1 ? '' : 's'}` : ''
  return `${label} · ${formatDateTime(alert.created_at)}${suffix}`
}

export function ReadingSelect({ readings, active }: { readings: Alert[]; active: Alert }) {
  const advisor = useAdvisor()
  return (
    <select
      className="advisor-reading-select"
      aria-label="Elegir lectura"
      value={active.id}
      onChange={(event) => advisor.openReading(Number(event.target.value))}
    >
      {readings.map((alert) => (
        <option key={alert.id} value={alert.id}>
          {readingLabel(alert)}
        </option>
      ))}
    </select>
  )
}

export function RequestFailure() {
  const advisor = useAdvisor()
  if (!advisor.failedRequest || advisor.isRequesting) return null
  const { presetId, message } = advisor.failedRequest
  return (
    <p className="advisor-error" role="alert">
      No se pudo pedir la lectura: {message}.{' '}
      <button
        type="button"
        className="button button--ghost button--small"
        onClick={() => void advisor.requestReading(presetId)}
      >
        Reintentar
      </button>
    </p>
  )
}

function FirstReadingInvitation() {
  const advisor = useAdvisor()
  const { data: presets } = usePortfolioAdvicePresets()
  return (
    <div className="advisor-empty">
      <p>Todavía no pediste ninguna lectura de la flota.</p>
      <div className="advisor-empty__presets">
        {(presets ?? []).map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="button button--ghost"
            disabled={advisor.isRequesting}
            onClick={() => void advisor.requestReading(preset.id)}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export function AdvisorPanel() {
  const advisor = useAdvisor()
  const { readings, active, isPending } = useActiveReading()

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Escape') return
    advisor.closePanel()
    document.querySelector<HTMLButtonElement>('.app-topbar .advice-menu > button')?.focus()
  }

  return (
    <aside
      className="advisor-panel"
      role="complementary"
      aria-label="Asesor de IA"
      onKeyDown={handleKeyDown}
    >
      <header className="advisor-panel__header">
        <Sparkles size={16} aria-hidden="true" className="advisor-panel__icon" />
        <h2>Asesor IA</h2>
        <div className="advisor-panel__actions">
          <button
            type="button"
            className="icon-button icon-button--neutral"
            onClick={() => advisor.setDisplayMode('modal')}
            aria-label="Ver en ventana"
            title="Ver en ventana"
          >
            <Maximize2 size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="icon-button icon-button--neutral"
            onClick={advisor.closePanel}
            aria-label="Cerrar asesor"
            title="Cerrar asesor"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        {active && <ReadingSelect readings={readings} active={active} />}
      </header>

      <div className="advisor-panel__body">
        {advisor.isRequesting && (
          <p className="advisor-analyzing" role="status">
            Analizando la flota… puede tardar hasta un minuto
          </p>
        )}
        <RequestFailure />
        {isPending ? (
          <p className="muted">Cargando…</p>
        ) : active ? (
          <AdvisorConversation key={active.id} alert={active} layout="narrow" />
        ) : (
          <FirstReadingInvitation />
        )}
      </div>
    </aside>
  )
}
